// =============================================================================
// ui/state/useCoachingRows.ts — the whole book, four columns.
// -----------------------------------------------------------------------------
// Feeds the Pre-Market "FOR YOU TODAY" card. Not a provider: it is read by one
// panel, on one phase of one tab, and hoisting it to the shell would make every
// tab pay for it at boot.
//
// ⚠ `rows` stays NULL on failure — never `[]`. An empty book and a failed read
// produce completely different screens (a new user vs an apology), and the card
// must not print "not enough trades yet" at someone with a thousand of them.
// =============================================================================

import { useEffect, useState } from 'react';
import { useSupabase } from './supabase';
import { loadCoachingUniverse, type CoachingRow } from '../../io/sync/coaching';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface CoachingRowsResult {
  phase: 'loading' | 'ready' | 'failed';
  rows: readonly CoachingRow[] | null;
  error: string | null;
}

export function useCoachingRows(): CoachingRowsResult {
  const { sb } = useSupabase();
  const [phase, setPhase] = useState<CoachingRowsResult['phase']>('loading');
  const [rows, setRows] = useState<readonly CoachingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);
    void (async () => {
      try {
        const r = await loadCoachingUniverse(sb);
        if (cancelled) return;
        setRows(r);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        setError(reportFailure('coaching rows', e));
        setPhase('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [sb]);

  return { phase, rows, error };
}
